import * as _ from 'lodash';
import apiService from '../service/apiService';
import * as UtilityFunctions from '../HelperMethods/UtilityFunctions';
import { AREA_TYPE } from '../components/taskSetup/AreaSetup/constants';
import * as DateUtils from '../HelperMethods/DateUtils';
import logUtils from '../HelperMethods/LogUtils';
import { upsertAreas } from './appActions';

export const types = {
  LOAD_AREA_SUCCESS: 'LOAD_AREA_SUCCESS',
  LOAD_RAW_AREA_SUCCESS: 'LOAD_RAW_AREA_SUCCESS',
  LOAD_ALL_STOPS_SUCCESS: 'LOAD_ALL_STOPS_SUCCESS',
  LOAD_STOP_SUCCESS: 'LOAD_STOP_SUCCESS',
  LOAD_AREA_USER_SUCCESS: 'LOAD_AREA_USER_SUCCESS',
  LOAD_SINGLE_AREA_SUCCESS: 'LOAD_SINGLE_AREA_SUCCESS',
  CREATE_AREA_SUCCESS: 'CREATE_AREA_SUCCESS',
  AREA_LOADING_START: 'AREA_LOADING_START',
  DELETE_AREA_SUCCESS: 'DELETE_AREA_SUCCESS',
  UPDATE_AREA_SUCCESS: 'UPDATE_AREA_SUCCESS',
};

export function loadAreasForFieldId(fieldID) {
  return (dispatch) => {
    dispatch({ type: types.AREA_LOADING_START });
    return apiService.getAssetsByFieldAndTypes(fieldID, ['area']).then(async (response) => {
      try {
        const areas = response.data.getAssetsByFieldAndTypes;
        areas.forEach((area) => {
          area.teams = area.teams || [];
          area.teams = area.teams.filter((team) => !team.isDeleted);
        });
        dispatch({ type: types.LOAD_RAW_AREA_SUCCESS, areas });
        const areaIds = areas.map((area) => area.id);
        const teamIDSet = new Set();
        const userIDSet = new Set();
        areas.forEach((area) => {
          if (area.teams) {
            area.teams.forEach((team) => {
              teamIDSet.add(team.teamId);
              if (team.resources) {
                team.resources.forEach((resource) => {
                  userIDSet.add(resource.userId);
                });
              }
            });
          }
        });
        const userIds = Array.from(userIDSet);
        const teamIds = Array.from(teamIDSet);
        const userByIdsPromises = [];
        while (userIds?.length) {
          const seed = userIds.splice(0, 1000);
          userByIdsPromises.push(apiService.getUsersByIDs(seed));
        }
        const responses = await Promise.all([
          apiService.getAssetByAreasAndTypes(areaIds, ['stop']),
          apiService.searchTeams(UtilityFunctions.getSearchGraphQLParamForFieldId(fieldID)),
          ...userByIdsPromises,
        ]);

        const stopList = _.get(responses, '[0].data.getAssetByAreasAndTypes', []);
        const allTeams = _.get(responses, '[1].data.searchTeams.items', []);
        let users = [];
        for (let i = 2; i < responses.length; i += 1) {
          const res = _.get(responses, `[${i}].data.getUserByIds`, []);
          users = [...users, ...res];
        }
        const teamsNeeded = allTeams.filter((team) => teamIds.indexOf(team.id) > -1);
        const areaStopResourceMap = UtilityFunctions.mapStopsToArea(areas, stopList, teamsNeeded, users);
        const tableData = [];
        const rowSpanAreaMap = {};

        Object.keys(areaStopResourceMap).forEach((areaId) => {
          let stops = null;
          if (areaStopResourceMap[areaId].stops) {
            stops = areaStopResourceMap[areaId].stops.map((stop) => stop.name).join(', ');
          }
          if (areaStopResourceMap[areaId].teams && areaStopResourceMap[areaId].teams.length > 0) {
            const areaStopResourceMapObj = areaStopResourceMap[areaId];
            const { teams, area } = areaStopResourceMapObj;
            const data = UtilityFunctions.areaTableData(teams, area, stops, rowSpanAreaMap);
            tableData.push(...data);
          } else {
            tableData.push({
              areaName: areaStopResourceMap[areaId].area.name,
              areaId: areaStopResourceMap[areaId].area.id,
              type: areaStopResourceMap[areaId].area.areaType,
              stops,
              teamName: null,
              primary: null,
              secondary: null,
              modifiedOn: DateUtils.convertUTCDateStringToYYYYMMDDHHMMSSFormat(
                areaStopResourceMap[areaId].area.modifiedOn
              ),
            });
          }
        });

        dispatch({ type: types.LOAD_AREA_SUCCESS, areas: tableData });
      } catch (e) {
        logUtils.logError(e, 'Error loading areas');
      }
    });
  };
}

export function getStopsByFieldID(fieldID) {
  return (dispatch) => {
    return apiService.getAssetsByFieldAndTypes(fieldID, ['stop']).then((response) => {
      dispatch({ type: types.LOAD_ALL_STOPS_SUCCESS, stops: response.data.getAssetsByFieldAndTypes });
    });
  };
}

export function getStopsByAreaIDs(areaIDs) {
  return (dispatch) => {
    return apiService.getAssetByAreasAndTypes(areaIDs, ['stop']).then((response) => {
      dispatch({ type: types.LOAD_STOP_SUCCESS, stops: response.data.getAssetByAreasAndTypes });
    });
  };
}

export function getUsersByIDs(IDs) {
  return (dispatch) => {
    return apiService.getUsersByIDs(IDs).then((response) => {
      dispatch({ type: types.LOAD_AREA_USER_SUCCESS, users: response.data.getUserByIds });
    });
  };
}

function getTeamsSelected(selectedTeamsMap) {
  return Object.values(selectedTeamsMap).map((team) => {
    const primaryResources = team.primary
      ? team.primary.map((id) => {
          return {
            userId: id,
            resourceLevel: 'primary',
          };
        })
      : [];
    const secondaryResources = team.secondary
      ? team.secondary.map((id) => {
          return {
            userId: id,
            resourceLevel: 'secondary',
          };
        })
      : [];
    return {
      teamId: team.id,
      resources: [...primaryResources, ...secondaryResources],
    };
  });
}

function getCreateUpdateAreaInput(area, selectedTeamsMap, fieldId) {
  const result = {};
  const teamsSelected = getTeamsSelected(selectedTeamsMap);

  result.name = area.name;
  result.type = 'area';
  result.fieldId = fieldId;
  result.teams = teamsSelected;
  result.areaType = area.areaType;
  if (result.areaType === AREA_TYPE.DYNAMIC) {
    result.areaDefinition = area.areaDefinition;
    result.additionalStopIds = area.stopsIds;
  }
  return result;
}

function updateAreaForStops(newAreaId, stopsListDict, stops, fieldId, deleteFlag) {
  if (!stops) {
    return;
  }
  stops = _.uniq(stops);
  stops.forEach((element) => {
    const oldAreas = stopsListDict[element].areas || [];
    const oldAreasDict = _.keyBy(oldAreas, 'areaId');
    const newArea = { areaId: newAreaId, isDeleted: deleteFlag };
    oldAreasDict[newAreaId] = newArea;
    const newAreasList = Object.values(oldAreasDict);
    const updateStopObject = {
      fieldId,
      id: element,
      areas: newAreasList,
    };
    apiService.updateAsset(updateStopObject);
  });
}

function updateAreasForStopsWrapper(newAreaId, stopsList, currentStops, fieldId, existingStops, isUpdate) {
  const stopsListDict = _.keyBy(stopsList, 'key');
  if (isUpdate) {
    const existingStopsOfAreaDict = _.keyBy(existingStops, (o) => {
      return o;
    });
    const currentStopsOfAreaDict = _.keyBy(currentStops, (o) => {
      return o;
    });
    let idsToDelete = [];
    for (const id in currentStopsOfAreaDict) {
      if (existingStopsOfAreaDict[id]) {
        idsToDelete = idsToDelete.concat([id]);
      }
    }
    idsToDelete.forEach((id) => {
      delete existingStopsOfAreaDict[id];
      delete currentStopsOfAreaDict[id];
    });
    updateAreaForStops(newAreaId, stopsListDict, Object.values(existingStopsOfAreaDict), fieldId, true);
    updateAreaForStops(newAreaId, stopsListDict, Object.values(currentStopsOfAreaDict), fieldId, false);
  } else {
    updateAreaForStops(newAreaId, stopsListDict, currentStops, fieldId, false);
  }
}

export function createArea({ area, selectedStops: stopsList, selectedTeamsMap }, fieldId) {
  return (dispatch) => {
    const createAreaInput = getCreateUpdateAreaInput(area, selectedTeamsMap, fieldId);

    return apiService.createAsset(createAreaInput).then((response) => {
      const savedArea = response.data.createAsset;
      const concatStops = stopsList.map((stop) => stop.text).join(', ');
      const teams = UtilityFunctions.formatUsersForAreaTable(Object.values(selectedTeamsMap));
      const data = UtilityFunctions.areaTableData(teams, savedArea, concatStops);
      dispatch({ type: types.CREATE_AREA_SUCCESS, area: data }); // Not needed as we are not using info from store
      if (savedArea.areaType !== AREA_TYPE.DYNAMIC) {
        updateAreasForStopsWrapper(savedArea.id, stopsList, area.stopsIds, fieldId, [], false);
      }
    });
  };
}

export function updateArea({ area, selectedStops: stopsList, selectedTeamsMap }, fieldId, existingStops, areaId) {
  return (dispatch) => {
    const updateAreaInput = getCreateUpdateAreaInput(area, selectedTeamsMap, fieldId);
    updateAreaInput.id = areaId;
    return apiService.updateAsset(updateAreaInput).then((response) => {
      const updatedArea = response.data.updateAsset;
      dispatch(upsertAreas([updatedArea]));
      const concatStops = stopsList.map((stop) => stop.text).join(', ');
      const teams = UtilityFunctions.formatUsersForAreaTable(Object.values(selectedTeamsMap));
      const data = UtilityFunctions.areaTableData(teams, area, concatStops);
      dispatch({ type: types.UPDATE_AREA_SUCCESS, area: data, areaId }); // Not needed as we are not using info from store
      if (updatedArea.areaType !== AREA_TYPE.DYNAMIC) {
        const existingStopsIds = existingStops.map((s) => s.key);
        updateAreasForStopsWrapper(
          updatedArea.id,
          [...stopsList, ...existingStops],
          area.stopsIds,
          fieldId,
          existingStopsIds,
          true
        );
      }
    });
  };
}
export function getAreaByAreaId(areaId) {
  return (dispatch) => {
    return apiService.getAsset(areaId).then((response) => {
      dispatch({ type: types.LOAD_SINGLE_AREA_SUCCESS, area: response.data.getAsset });
      return response.data.getAsset;
    });
  };
}

export function deleteArea(id) {
  return (dispatch) => {
    dispatch({ type: types.DELETE_AREA_SUCCESS, id });
  };
}
